import { faFileImport, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  type ClipboardEvent as ReactClipboardEvent,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useId,
  useState,
} from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { api, ApiClientError } from "../api";
import { useAuth } from "../auth";
import { localDateInputValue } from "../date";
import { normalizeSourceUrl } from "../source-url";

interface DraftItem {
  agendaNumber: string;
  description: string;
  sourceUrl: string;
  sourceLabel: string;
  roundLabels: string;
}

const itemColumns = [
  "agendaNumber",
  "description",
  "sourceUrl",
  "sourceLabel",
] as const;

function roundLabelsFor(hearingType: string): string[] {
  if (hearingType === "two_vote") return ["First hearing", "Final hearing"];
  if (hearingType === "three_vote")
    return ["First hearing", "Second hearing", "Final hearing"];
  if (hearingType === "four_vote")
    return [
      "First hearing",
      "Second hearing",
      "Third hearing",
      "Final hearing",
    ];
  return ["Position"];
}

function blankItems(count = 8, hearingType = "simple"): DraftItem[] {
  return Array.from({ length: count }, () => ({
    agendaNumber: "",
    description: "",
    sourceUrl: "",
    sourceLabel: "",
    roundLabels: roundLabelsFor(hearingType).join(" | "),
  }));
}

export function NewDecisionPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const tableHelpId = useId();
  const [hearingType, setHearingType] = useState("simple");
  const [items, setItems] = useState<DraftItem[]>(() => blankItems());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (loading && !user)
    return (
      <div className="page-section">
        <div className="container loading-block" role="status">
          Checking access…
        </div>
      </div>
    );
  if (!user)
    return <Navigate to="/sign-in" replace state={{ from: "/guides/new" }} />;
  if (
    !user.isSystemAdmin &&
    user.account.role !== "owner" &&
    user.account.role !== "manager"
  )
    return (
      <div className="page-section">
        <div className="container empty-state">
          <h1>Manager access required</h1>
          <p>An owner or manager can create an organization guide.</p>
          <Link className="button" to="/library?scope=mine">
            Return to your guides
          </Link>
        </div>
      </div>
    );
  const changeType = (value: string) => {
    setHearingType(value);
    setItems((current) =>
      current.map((item) => ({
        ...item,
        roundLabels:
          value === "flexible"
            ? item.roundLabels || "Position"
            : roundLabelsFor(value).join(" | "),
      })),
    );
  };
  const updateItem = <K extends keyof DraftItem>(
    index: number,
    key: K,
    value: DraftItem[K],
  ) =>
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item,
      ),
    );
  const visibleColumns =
    hearingType === "flexible"
      ? ([...itemColumns, "roundLabels"] as const)
      : itemColumns;
  const pasteItems = (
    event: ReactClipboardEvent<HTMLInputElement>,
    rowIndex: number,
    columnIndex: number,
  ) => {
    const raw = event.clipboardData.getData("text/plain");
    if (!raw.includes("\t") && !/[\r\n]/.test(raw)) return;
    event.preventDefault();
    const pastedRows = raw
      .replaceAll("\r\n", "\n")
      .replaceAll("\r", "\n")
      .split("\n");
    if (pastedRows.at(-1) === "") pastedRows.pop();
    setItems((current) => {
      const required = rowIndex + pastedRows.length;
      const next = [
        ...current.map((item) => ({ ...item })),
        ...blankItems(Math.max(0, required - current.length), hearingType),
      ];
      pastedRows.forEach((line, pastedRowIndex) => {
        line.split("\t").forEach((value, pastedColumnIndex) => {
          const column = visibleColumns[columnIndex + pastedColumnIndex];
          const target = next[rowIndex + pastedRowIndex];
          if (column && target) target[column] = value.trim();
        });
      });
      return next;
    });
  };
  const extendOnTab = (
    event: ReactKeyboardEvent<HTMLInputElement>,
    rowIndex: number,
    columnIndex: number,
  ) => {
    if (
      event.key !== "Tab" ||
      event.shiftKey ||
      columnIndex !== visibleColumns.length - 1 ||
      rowIndex !== items.length - 1
    )
      return;
    event.preventDefault();
    setItems((current) => [...current, ...blankItems(1, hearingType)]);
    window.setTimeout(() => {
      document
        .querySelector<HTMLInputElement>(
          `[data-new-guide-cell="${String(rowIndex + 1)}-0"]`,
        )
        ?.focus();
    });
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const enteredItems = items.filter((item) =>
        itemColumns.some((column) => item[column].trim()),
      );
      if (!enteredItems.length) {
        setError("Enter at least one agenda item.");
        setBusy(false);
        return;
      }
      if (enteredItems.length > 5_000) throw new Error("TOO_MANY_AGENDA_ITEMS");
      const agendaNumbers = new Set<string>();
      const preparedItems = enteredItems.map((item) => {
        const agendaNumber = item.agendaNumber.trim() || null;
        const description = item.description.trim();
        if (!agendaNumber && !description)
          throw new Error("AGENDA_ITEM_REQUIRED");
        if (agendaNumber) {
          const normalized = agendaNumber.toUpperCase();
          if (agendaNumbers.has(normalized))
            throw new Error("DUPLICATE_AGENDA_NUMBER");
          agendaNumbers.add(normalized);
        }
        if (item.sourceLabel.trim() && !item.sourceUrl.trim())
          throw new Error("SOURCE_LABEL_REQUIRES_URL");
        const labels =
          hearingType === "flexible"
            ? item.roundLabels
                .split("|")
                .map((label) => label.trim())
                .filter(Boolean)
            : roundLabelsFor(hearingType);
        if (
          !labels.length ||
          labels.length > 20 ||
          labels.some((label) => label.length > 160)
        )
          throw new Error("INVALID_ROUNDS");
        return {
          agendaNumber,
          description,
          sourceUrl: normalizeSourceUrl(item.sourceUrl),
          sourceLabel: item.sourceLabel.trim() || null,
          rounds: labels.map((label) => ({
            label,
            description: "",
            occurredOn: null,
          })),
        };
      });
      const created = await api<{ accountSlug: string; code: string }>(
        "/api/guides",
        {
          method: "POST",
          body: JSON.stringify({
            title: form.get("title"),
            description: form.get("description"),
            guideDate: form.get("guideDate"),
            hearingType,
            visibility: form.get("visibility"),
            items: preparedItems,
          }),
        },
      );
      navigate(`/guides/${created.accountSlug}/${created.code}`);
    } catch (caught) {
      setError(
        caught instanceof Error && caught.message === "INVALID_SOURCE_URL"
          ? "Enter a valid source URL."
          : caught instanceof Error &&
              caught.message === "DUPLICATE_AGENDA_NUMBER"
            ? "Agenda numbers must be unique within the guide."
            : caught instanceof Error && caught.message === "INVALID_ROUNDS"
              ? "Enter between one and 20 hearing round labels. Each label can contain up to 160 characters."
              : caught instanceof Error &&
                  caught.message === "TOO_MANY_AGENDA_ITEMS"
                ? "A guide can contain up to 5,000 agenda items."
                : caught instanceof Error &&
                    caught.message === "SOURCE_LABEL_REQUIRES_URL"
                  ? "Enter a source URL before adding a source label."
                  : caught instanceof Error &&
                      caught.message === "AGENDA_ITEM_REQUIRED"
                    ? "Each row needs an agenda number or description."
                    : caught instanceof ApiClientError
                      ? caught.message
                      : "Unable to create guide.",
      );
      setBusy(false);
    }
  };
  return (
    <div className="page-section">
      <div className="container form-container wide-form">
        <div className="page-heading">
          <div>
            <p className="kicker">New position guide</p>
            <h1>Create a guide</h1>
          </div>
          <Link className="button button-secondary" to="/import">
            <FontAwesomeIcon icon={faFileImport} /> Import CSV or PDF
          </Link>
        </div>
        <form
          className="panel form-grid"
          onSubmit={(event) => void submit(event)}
        >
          <label className="field-span">
            Guide title
            <input name="title" required minLength={3} maxLength={240} />
          </label>
          <label className="field-span">
            Guide description
            <textarea name="description" required rows={5} />
          </label>
          <label>
            Guide date
            <input
              name="guideDate"
              type="date"
              required
              defaultValue={localDateInputValue()}
            />
          </label>
          <label>
            Visibility
            <select name="visibility" defaultValue="private">
              <option value="private">Private</option>
              <option value="public">Public when published</option>
            </select>
          </label>
          <label>
            Hearing structure
            <select
              value={hearingType}
              onChange={(event) => changeType(event.target.value)}
            >
              <option value="simple">Single round</option>
              <option value="two_vote">Two rounds</option>
              <option value="three_vote">Three rounds</option>
              <option value="four_vote">Four rounds</option>
              <option value="flexible">Flexible</option>
            </select>
          </label>
          <fieldset className="field-span new-guide-item-grid">
            <legend>Agenda items</legend>
            <div className="bulk-editor-heading">
              <span id={tableHelpId}>
                Paste spreadsheet rows or move through cells with Tab. Blank
                rows are ignored.
              </span>
            </div>
            <div className="spreadsheet-table-wrap new-guide-table-wrap">
              <table
                className="spreadsheet-table new-guide-table"
                aria-describedby={tableHelpId}
              >
                <caption className="sr-only">
                  Agenda items for the new guide
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Agenda item</th>
                    <th scope="col">Description</th>
                    <th scope="col">Source URL</th>
                    <th scope="col">Source label</th>
                    {hearingType === "flexible" ? (
                      <th scope="col">Hearing rounds</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, itemIndex) => (
                    <tr key={itemIndex}>
                      {visibleColumns.map((column, columnIndex) => (
                        <td key={column}>
                          <input
                            aria-label={`${
                              column === "agendaNumber"
                                ? "Agenda item"
                                : column === "sourceUrl"
                                  ? "Source URL"
                                  : column === "sourceLabel"
                                    ? "Source label"
                                    : column === "roundLabels"
                                      ? "Hearing rounds"
                                      : "Description"
                            } row ${String(itemIndex + 1)}`}
                            data-new-guide-cell={`${String(itemIndex)}-${String(columnIndex)}`}
                            inputMode={
                              column === "sourceUrl" ? "url" : undefined
                            }
                            autoComplete="off"
                            maxLength={
                              column === "agendaNumber"
                                ? 80
                                : column === "sourceLabel"
                                  ? 160
                                  : column === "sourceUrl"
                                    ? 2_048
                                    : column === "roundLabels"
                                      ? 3_219
                                      : 50_000
                            }
                            placeholder={
                              column === "agendaNumber"
                                ? "A28-99"
                                : column === "roundLabels"
                                  ? "Initial hearing | Final hearing"
                                  : undefined
                            }
                            value={item[column]}
                            onChange={(event) =>
                              updateItem(itemIndex, column, event.target.value)
                            }
                            onPaste={(event) =>
                              pasteItems(event, itemIndex, columnIndex)
                            }
                            onKeyDown={(event) =>
                              extendOnTab(event, itemIndex, columnIndex)
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </fieldset>
          <button
            type="button"
            className="text-button field-span"
            onClick={() =>
              setItems((current) => [...current, ...blankItems(1, hearingType)])
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Add row
          </button>
          {error ? (
            <div className="alert alert-error field-span" role="alert">
              {error}
            </div>
          ) : null}
          <div className="form-actions field-span">
            <Link className="button button-ghost" to="/library?scope=mine">
              Cancel
            </Link>
            <button className="button" disabled={busy}>
              {busy ? "Creating…" : "Create private draft"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
